/**
 * Helpers for the "booking date" a customer chose via the service's primary-date
 * custom field. The API delivers it as a bare `yyyy-MM-dd` string (`preferredDate`).
 *
 * `new Date("2026-07-20")` parses as UTC midnight and shifts the day backward in
 * positive-offset zones (e.g. PH, +08:00 → shows Jul 19). We build a LOCAL date
 * from the parts instead so the calendar day matches what the customer picked.
 */
export function parseDateLocal(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/**
 * Human label for a booking-date value. "Date" fields are `yyyy-MM-dd` →
 * "Jul 20, 2026". "Date and Time" fields are `yyyy-MM-ddTHH:mm` → include the
 * time, "Jul 20, 2026, 2:30 PM". Falls back to the raw string.
 */
export function formatPreferredDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d, hh, mm] = m;
  const hasTime = hh != null && mm != null;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    hasTime ? Number(hh) : 0,
    hasTime ? Number(mm) : 0,
  );
  return hasTime
    ? date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

/** Just the time part ("2:30 PM") if the value carries one, else "". */
export function formatTimeMaybe(iso: string): string {
  const m = /[T ](\d{2}):(\d{2})/.exec(iso);
  if (!m) return "";
  const d = new Date(2000, 0, 1, Number(m[1]), Number(m[2]));
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
